
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ModulesList } from "./modules/ModulesList";
import { AddModuleButton } from "./modules/AddModuleButton";
import { TranslateCourseContentDialog } from "./courses/translation/TranslateCourseContentDialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { toast } from "sonner";

type CourseModulesProps = {
  courseId: string;
};

// Define the module type to match the data structure
type ModuleType = {
  id: string;
  module_number: number;
  part_number: number;
  title: string;
  duration_minutes: number;
  content?: string | null;
  is_exam?: boolean | null;
  course_id: string;
  updated_at: string | null;
};

export function CourseModules({ courseId }: CourseModulesProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { isAdmin } = useAdminAuth();
  const queryClient = useQueryClient();
  const [moduleChanges, setModuleChanges] = useState<Record<string, any>>({});
  
  const { data: modules, isLoading, error } = useQuery({
    queryKey: ['courseModules', courseId],
    queryFn: async () => {
      console.log('Fetching modules for course:', courseId);
      
      // Using the custom RPC function to bypass RLS
      // We need to use any here as the TypeScript definitions don't include our custom functions yet
      const { data, error } = await (supabase as any)
        .rpc('get_course_modules_by_course_id', { p_course_id: courseId });
      
      if (error) {
        console.error('Error fetching modules:', error);
        toast.error('Failed to load course modules');
        throw error;
      }
      
      console.log('Modules data:', data);
      // Type assertion to help TypeScript understand the data structure
      return (data || []) as ModuleType[];
    },
    retry: 3,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    staleTime: 0
  });

  const handleModuleChange = (moduleId: string, data: any) => {
    setModuleChanges(prev => ({
      ...prev,
      [moduleId]: data
    }));
  };

  const handleSave = async () => {
    if (Object.keys(moduleChanges).length === 0) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      console.log('Saving module changes:', moduleChanges);
      
      // Create an array of update operations
      const updates = Object.entries(moduleChanges).map(([moduleId, data]) => ({
        id: moduleId,
        module_number: data.module_number,
        part_number: data.part_number,
        title: data.title,
        duration_minutes: data.duration_minutes,
        course_id: courseId,
        updated_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('course_modules')
        .upsert(updates)
        .select();

      if (error) {
        console.error('Error saving modules:', error);
        throw error;
      }

      toast.success('Changes saved successfully');
      setModuleChanges({});
      await queryClient.invalidateQueries({ queryKey: ['courseModules', courseId] });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle>Course Modules</CardTitle>
        <div className="flex flex-wrap space-x-2">
          <TranslateCourseContentDialog courseId={courseId} contentType="modules" />
          {!isEditing ? (
            <Button variant="pill" onClick={() => setIsEditing(true)}>Edit</Button>
          ) : (
            <>
              <Button 
                variant="pill" 
                className="bg-white text-black border border-black hover:bg-black/10 dark:bg-black dark:text-white dark:border-white dark:hover:bg-white/10"
                onClick={() => {
                  setModuleChanges({});
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="pill" 
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="text-center py-6">
            <p className="text-destructive mb-4">Error loading modules: {(error as Error).message}</p>
            <Button 
              variant="outline" 
              onClick={() => queryClient.invalidateQueries({ queryKey: ['courseModules', courseId] })}
            >
              Retry
            </Button>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (!modules || modules.length === 0) ? (
          <div className="text-center py-6">
            <p className="text-muted-foreground mb-4">No modules found for this course.</p>
            {isEditing && isAdmin && (
              <AddModuleButton
                courseId={courseId}
                modules={modules}
                className="mt-4"
              />
            )}
          </div>
        ) : (
          <>
            <ModulesList 
              courseId={courseId} 
              isEditing={isEditing} 
              onModuleChange={handleModuleChange}
            />
            {isEditing && isAdmin && (
              <AddModuleButton
                courseId={courseId}
                modules={modules}
                className="mt-4 w-full"
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
